import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";

import readOnlySelect from "fond/draw/modes/base/readOnlySelect";
import { lineString } from "fond/turf";

import { buildEndpointIndex, EndpointIndex, findConnectedChain } from "./chainSelection";
import { getRoadLayerIds, METERS_PER_DEG_LAT } from "./constants";
import { buildFeatureBBoxIndex, findConnectedCrossings, findParallelFeatures, RoadSelectionIndex } from "./roadSelection";
import { extractRoadCoords } from "./selectRoad";

const {
  constants: Constants,
  lib: {
    doubleClickZoom,
    CommonSelectors: { isFeature, isShiftDown },
  },
} = MapboxDraw;

interface SelectSidesState {
  endpointIndex: EndpointIndex;
  featureIndex: RoadSelectionIndex;
  roadLayerIds: string[];
  [key: string]: unknown;
}

/**
 * Runtime methods available on the draw mode `this` context but not
 * fully represented in the @mapbox/mapbox-gl-draw type definitions.
 */
interface DrawModeThis extends DrawCustomModeThis {
  deselect(id: string | number): void;
  stopExtendedInteractions(state: unknown): void;
}

// readOnlySelect typed with clickOnFeature available (inherited from simple_select at runtime)
type ReadOnlySelectWithClick = DrawCustomMode & {
  clickOnFeature?(this: DrawModeThis, state: SelectSidesState, event: MapboxDraw.MapMouseEvent): void;
};

/** Search radius in meters for finding a nearby basemap road */
const ROAD_SEARCH_RADIUS = 30;

/**
 * Query the basemap for a road near the click point, then return all draw
 * features on the same side of that road as the clicked feature.
 * Falls back to just the clicked feature ID if no road is found.
 */
function findRoadParallelIds(map: mapboxgl.Map, state: SelectSidesState, event: MapboxDraw.MapMouseEvent): Set<string> {
  const featureId = String(event.featureTarget.properties?.id);

  if (state.roadLayerIds.length === 0) return new Set([featureId]);

  // Pad the click location by ROAD_SEARCH_RADIUS meters in geographic
  // coordinates, then project to screen space for queryRenderedFeatures.
  const { lng, lat } = event.lngLat;
  const latPad = ROAD_SEARCH_RADIUS / METERS_PER_DEG_LAT;
  const lngPad = ROAD_SEARCH_RADIUS / (METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));

  const sw = map.project([lng - lngPad, lat - latPad]);
  const ne = map.project([lng + lngPad, lat + latPad]);
  const hitBox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [Math.min(sw.x, ne.x), Math.min(sw.y, ne.y)],
    [Math.max(sw.x, ne.x), Math.max(sw.y, ne.y)],
  ];
  const roadFeatures = map.queryRenderedFeatures(hitBox, { layers: state.roadLayerIds });
  const roadFeature = roadFeatures[0] ?? null;

  if (!roadFeature) return new Set([featureId]);

  // Extract the relevant LineString from the road (handles MultiLineString)
  const roadCoords = extractRoadCoords(map, roadFeature, event.point);
  const road = lineString(roadCoords);

  const parallelIds = findParallelFeatures(state.featureIndex, road);

  if (parallelIds.size === 0) return new Set([featureId]);

  // Use chain connectivity to determine which side the clicked feature is on.
  // Features on the same side are connected via co-located endpoints;
  // features on the opposite side are not (crossings act as chain boundaries).
  const chain = findConnectedChain(state.endpointIndex, featureId);

  // Intersect: keep only parallel features that are in the same chain.
  // Always include the clicked feature itself.
  const sameIds = new Set<string>([featureId]);
  for (const id of parallelIds) {
    if (chain.has(id)) {
      sameIds.add(id);
    }
  }

  // Pick up crossings that connect to features on the selected side only
  const sideCrossings = findConnectedCrossings(state.endpointIndex, sameIds);
  return new Set([...sameIds, ...sideCrossings]);
}

/**
 * A custom draw mode that selects sides on the same road.
 * Clicking a LineString finds the nearest basemap road, then selects
 * all draw features that run parallel to it (plus connected crossings).
 * Falls back to selecting only the clicked feature if no road is found.
 *
 * Shift-click on an unselected feature adds its road-matched group to the selection.
 * Shift-click on a selected feature removes only that feature.
 * Box select expands each feature in the box to its full connected chain.
 */
const selectSides = {
  ...readOnlySelect,

  onSetup(this: DrawModeThis) {
    const parentState = (readOnlySelect as ReadOnlySelectWithClick).onSetup?.call(this as unknown as DrawCustomModeThis & DrawCustomMode, {}) ?? {};
    const allFeatures = this._ctx.api.getAll().features;

    const roadLayerIds = getRoadLayerIds(this.map);

    return {
      ...(parentState as object),
      endpointIndex: buildEndpointIndex(allFeatures),
      featureIndex: buildFeatureBBoxIndex(allFeatures),
      roadLayerIds,
    } as SelectSidesState;
  },

  onClick(
    this: DrawModeThis & { clickOnFeature(state: SelectSidesState, event: MapboxDraw.MapMouseEvent): void },
    state: SelectSidesState,
    event: MapboxDraw.MapMouseEvent
  ) {
    if (isFeature(event)) {
      this.clickOnFeature(state, event);
      return;
    }
    // Click on empty space — clear selection and force full re-render
    this.clearSelectedFeatures();
    const store = this._ctx.store as unknown as { setDirty(): void; render(): void };
    store.setDirty();
    store.render();
    doubleClickZoom.enable(this);
  },

  onMouseUp(this: DrawModeThis, state: SelectSidesState, event: MapboxDraw.MapMouseEvent) {
    // Let parent (simple_select) handle box select completion — it selects
    // individual features in the box. No expansion beyond what the box touches.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readOnlySelect.onMouseUp?.call(this as any, state, event);
  },

  onTouchEnd(this: DrawModeThis, state: SelectSidesState, event: MapboxDraw.MapTouchEvent) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (selectSides as any).onMouseUp.call(this, state, event as unknown as MapboxDraw.MapMouseEvent);
  },

  clickOnFeature(this: DrawModeThis, state: SelectSidesState, event: MapboxDraw.MapMouseEvent) {
    doubleClickZoom.disable(this);
    this.stopExtendedInteractions(state);

    const featureId = event.featureTarget.properties?.id;
    if (!featureId) return;

    const feature = this.getFeature(featureId);
    if (!feature || feature.type !== Constants.geojsonTypes.LINE_STRING) {
      (readOnlySelect as ReadOnlySelectWithClick).clickOnFeature?.call(this, state, event);
      return;
    }

    // Find the road under/near the clicked side
    const matchIds = findRoadParallelIds(this.map, state, event);

    const isShiftClick = isShiftDown(event);
    const isFeatureSelected = this.isSelected(featureId);

    if (isShiftClick && isFeatureSelected) {
      // Shift-click on selected: remove only the clicked feature
      this.deselect(featureId);
      if (this.getSelectedIds().length === 0) {
        doubleClickZoom.enable(this);
      }
    } else if (isShiftClick) {
      // Shift-click on unselected: add road-matched features to selection
      for (const id of matchIds) {
        if (!this.isSelected(id)) {
          this.select(id);
        }
      }
    } else {
      // Regular click: replace selection with road-matched features
      const ids = Array.from(matchIds);
      this.setSelected(ids[0]);
      for (let i = 1; i < ids.length; i++) {
        this.select(ids[i]);
      }
    }

    // Force a full re-render of all features so active/inactive styling updates correctly
    const store = this._ctx.store as unknown as { setDirty(): void; render(): void };
    store.setDirty();
    store.render();
  },
} as unknown as DrawCustomMode;

export default selectSides;
